import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Award, TrendingUp, Globe2, Clock, Filter } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import type { CaseIndustry } from "@/content/case-studies/registry";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildSpeakableSchema } from "@/lib/schema";

type CaseItem = {
  slug: string;
  title: string;
  tldr: string;
  timeline: string;
  location: string;
  industry: CaseIndustry;
  industryLabel: string;
  metrics: { label: string; value: string }[];
};
type CaseLoaderData = {
  cases: CaseItem[];
  industries: { id: CaseIndustry; label: string }[];
};

export const Route = createFileRoute("/case-studies/")({
  component: CaseStudiesHub,
  loader: async (): Promise<CaseLoaderData> => {
    const { CASE_STUDY_META, CASE_INDUSTRY_LIST } = await import(
      "@/content/case-studies/meta.gen"
    );
    return {
      cases: CASE_STUDY_META.map((c) => ({
        slug: c.slug,
        title: c.title,
        tldr: c.tldr,
        timeline: c.timeline,
        location: c.location,
        industry: c.industry,
        industryLabel: c.industryLabel,
        metrics: c.metrics.slice(0, 3).map((m) => ({ label: m.label, value: m.value })),
      })),
      industries: CASE_INDUSTRY_LIST.map((i) => ({ id: i.id, label: i.label })),
    };
  },
  head: ({ loaderData }) => {
    const site = getActiveSiteUrl();
    const cases: CaseItem[] = loaderData?.cases ?? [];
    const base = pageHeadForProvince({
      title: `Case Studies — Notary, Work Permit, Marriage, Translation`,
      description: `รวมเคสจริง ${cases.length}+ เคสจาก 10 อุตสาหกรรม — Work Permit batch, NAATI rush, marriage international, POA real-estate, M&A และอื่นๆ พร้อม KPI และ timeline จริง`,
      path: "/case-studies",
    });
    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/case-studies")) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: "/" },
              { name: "เคสจริง", url: "/case-studies" },
            ]),
          ),
        },
        ...(cases.length
          ? [{
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Case Studies",
            itemListElement: cases.map((c, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `${site}/case-studies/${c.slug}`,
              name: c.title,
            })),
          }),
          }]
          : []),
      ],
    };
  },
});

function CaseStudiesHub() {
  const { cases: CASE_STUDIES, industries: CASE_INDUSTRIES } =
    Route.useLoaderData() as CaseLoaderData;
  const [filter, setFilter] = useState<CaseIndustry | "all">("all");
  const filtered =
    filter === "all"
      ? CASE_STUDIES
      : CASE_STUDIES.filter((c) => c.industry === filter);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-6xl px-4 py-14">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gold">
              <Award className="h-3.5 w-3.5" /> Real engagements · Anonymized
            </div>
            <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">
              เคสจริง · Case Studies — งานที่เราทำสำเร็จ
            </h1>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">
              {CASE_STUDIES.length}+ เคสที่เลือกมาเล่าจาก 10 อุตสาหกรรม — แต่ละเคสมี
              ปัญหา, วิธีแก้, KPI, timeline และเอกสารจริง (anonymized) เพื่อให้คุณ
              ประเมินได้ว่าเราเหมาะกับงานของคุณหรือไม่
            </p>
            <div className="mt-6 grid max-w-2xl grid-cols-2 gap-3 md:grid-cols-4">
              <Stat icon={Award} label="เคสที่เผยแพร่" value={`${CASE_STUDIES.length}+`} />
              <Stat icon={Globe2} label="อุตสาหกรรม" value={`${CASE_INDUSTRIES.length}`} />
              <Stat
                icon={Clock}
                label="เคสด่วน ≤ 5 วัน"
                value={`${CASE_STUDIES.filter((c) => /1 วัน|48|36|3 วัน|5 วัน/.test(c.timeline)).length}`}
              />
              <Stat icon={TrendingUp} label="Repeat Clients" value="86%" />
            </div>
          </div>
        </section>

        <section className="sticky top-0 z-20 border-b border-border bg-background/95 backdrop-blur">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-2 px-4 py-3">
            <span className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <Filter className="h-3.5 w-3.5" /> กรอง
            </span>
            <button
              onClick={() => setFilter("all")}
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                filter === "all"
                  ? "border-gold bg-gold text-foreground"
                  : "border-border bg-card text-muted-foreground hover:border-gold"
              }`}
            >
              ทั้งหมด ({CASE_STUDIES.length})
            </button>
            {CASE_INDUSTRIES.map((ind) => {
              const count = CASE_STUDIES.filter((c) => c.industry === ind.id).length;
              if (count === 0) return null;
              return (
                <button
                  key={ind.id}
                  onClick={() => setFilter(ind.id)}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                    filter === ind.id
                      ? "border-gold bg-gold text-foreground"
                      : "border-border bg-card text-muted-foreground hover:border-gold"
                  }`}
                >
                  {ind.label} ({count})
                </button>
              );
            })}
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto grid max-w-6xl gap-5 px-4 py-10 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((c) => (
              <Link
                key={c.slug}
                to="/case-studies/$slug"
                params={{ slug: c.slug }}
                className="group flex flex-col rounded-2xl border border-border bg-card p-5 transition hover:-translate-y-0.5 hover:border-gold hover:shadow-lg"
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-gold">
                  {c.industryLabel}
                </div>
                <h2 className="mt-1 text-base font-extrabold leading-snug text-foreground group-hover:text-primary">
                  {c.title}
                </h2>
                <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{c.tldr}</p>
                <div className="mt-auto pt-4 text-xs text-muted-foreground">
                  <span className="font-bold text-foreground">{c.timeline}</span>
                  <span className="mx-1">·</span>
                  <span>{c.location}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {c.metrics.map((m, i) => (
                    <span
                      key={i}
                      className="rounded bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground"
                    >
                      {m.label}: {m.value}
                    </span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Award;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
      <div className="rounded-lg bg-primary/10 p-2 text-primary">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <div className="text-sm font-bold">{value}</div>
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
      </div>
    </div>
  );
}